import { Button, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { Workstation24Px, AlertBanner } from "@qubit/autoparts";

import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { useFcNavigate } from "~/hooks/useFcNavigate";

import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";

import EventFeed from "./EventFeed";

export const TESTING_TOOL_STACK_BREAKPOINT = "1300px";

type TestingToolLayoutProps = {
  children: React.ReactNode;
};

const TestingToolLayout = (props: TestingToolLayoutProps) => {
  const { children } = props;
  const navigate = useFcNavigate();
  const { t } = useTranslation();
  const workstation = useAppSelector(selectThisWorkstation);
  const shouldRenderNoWsMessage = !workstation;

  return (
    <Box
      sx={[
        {
          // Fill remaining viewport below a fixed 150px header
          height: "calc(100vh - 150px)",
          // Two-row & two-column grid
          // Test content on left, event feed on bottom-right
          display: "grid",
          justifyItems: "center",
          alignItems: "center",
          gridAutoFlow: "column",
          gridTemplateRows: "auto 1fr",
          // minmax(0, 1fr): main column can shrink instead of pushing into the sidebar
          // minmax(280px, 400px): cap sidebar width for consistent message wrapping at 1920px
          gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 400px)",
          gap: 2,
          overflow: "hidden",
          // Stack before ~1300px where multi port grids + sidebar min-widths collide
          [`@media (max-width: ${TESTING_TOOL_STACK_BREAKPOINT})`]: {
            height: "auto",
            minHeight: "calc(100vh - 150px)",
            gridTemplateColumns: "1fr",
            gap: 1
          }
        }
      ]}
    >
      {/* Main Testing Tool content */}
      <Box
        sx={{
          height: "100%",
          width: "100%",
          minWidth: 0,
          gridColumn: "1/2",
          gridRow: "1/3",
          boxSizing: "border-box",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            display: "none"
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          [`@media (max-width: ${TESTING_TOOL_STACK_BREAKPOINT})`]: {
            gridColumn: "1/-1",
            gridRow: "1/2"
          },
          ...(shouldRenderNoWsMessage && {
            display: "flex",
            justifyContent: "center"
          })
        }}
      >
        {shouldRenderNoWsMessage ? (
          <Stack
            sx={{
              flexDirection: "column",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              [`@media (max-width: ${TESTING_TOOL_STACK_BREAKPOINT})`]: {
                my: 3,
                gap: 0
              }
            }}
            role="region"
            aria-label="No workstation selected message"
          >
            <AlertBanner>{`${t("no workstation selected")}`}</AlertBanner>
            <Button
              color="info"
              sx={{
                minWidth: 250,
                minHeight: 70,
                fontSize: "1.5rem",
                mt: 3
              }}
              onClick={() =>
                navigate("/settings?settings-tab=workstation-config")
              }
              startIcon={<Workstation24Px fill="white" />}
            >
              {t("assign workstation")}
            </Button>
            <Typography
              variant="subtitle1"
              fontStyle="italic"
              fontWeight={400}
              textAlign="center"
            >
              {t("workstation warning info")}
            </Typography>
          </Stack>
        ) : (
          children
        )}
      </Box>
      {/*Event feed sidebar */}
      <EventFeed />
    </Box>
  );
};

export default TestingToolLayout;
